import React from "react";
import dynamic from "next/dynamic";
import { SectionProps } from "src/components/base";
import { AreaOverviewModuleContainer } from "src/components/modules/base/area-overview-module-container";
import { getCtaModuleColorScheme } from "src/components/modules/cta-module/color-scheme";
import { getHeaderModuleColorScheme } from "src/components/modules/header-module/color-scheme";
import { AREA_BOTTOM_MARGIN, AREA_TOP_MARGIN } from "src/constants/styles";
import { ContentModuleType, IAreaOverviewModule } from "src/types/area-overview";

const AutoContentModule = dynamic(() => import("src/components/modules/auto-content-module/auto-content-module").then(m => m.AutoContentModule));
const SingleBestSellerModule = dynamic(() =>
    import("src/components/modules/bestseller-module/single-bestseller-module").then(m => m.SingleBestSellerModule)
);
const ThreeBestsellerModule = dynamic(() =>
    import("src/components/modules/bestseller-module/three-bestseller-module").then(m => m.ThreeBestsellerModule)
);
const BlogTeaserModule = dynamic(() => import("src/components/modules/blog-teaser-module").then(m => m.BlogTeaserModule));
const BubbleChartModule = dynamic(() => import("src/components/modules/bubble-chart-module/bubble-chart-module").then(m => m.BubbleChartModule));
const ChallengeFormModule = dynamic(() =>
    import("src/components/modules/challenge-form-module/challenge-form-module").then(m => m.ChallengeFormModule)
);
const CookiebotDeclarationModule = dynamic(() =>
    import("src/components/modules/cookiebot-declaration-module/cookiebot-declaration-module").then(m => m.CookiebotDeclarationModule)
);
const CtaModule = dynamic(() => import("src/components/modules/cta-module/cta-module").then(m => m.CtaModule));
const DisclaimerModule = dynamic(() => import("src/components/modules/disclaimer-module/disclaimer-module").then(m => m.DisclaimerModule));
const FaqModule = dynamic(() => import("src/components/modules/faq-module/faq-module").then(m => m.FaqModule));
const FeesModule = dynamic(() => import("src/components/modules/fees-module/fees-module").then(m => m.FeesModule));
const HeaderForHomeModule = dynamic(() =>
    import("src/components/modules/header-for-home-module/header-for-home-module").then(m => m.HeaderForHomeModule)
);
const HeaderModule = dynamic(() => import("src/components/modules/header-module/header-module").then(m => m.HeaderModule));
const ImageModule = dynamic(() => import("src/components-mui/modules/image-module/image-module").then(m => m.ImageModule));
const ImageVideoModule = dynamic(() => import("src/components/modules/image-video-module/image-video-module").then(m => m.ImageVideoModule));
const JobsWidgetModule = dynamic(() => import("src/components/modules/jobs-widget-module/jobs-widget-module").then(m => m.JobsWidgetModule));
const ListModule = dynamic(() => import("src/components/modules/list-module/list-module").then(m => m.ListModule));
const MarketoModule = dynamic(() => import("src/components/modules/marketo-module/marketo-module").then(m => m.MarketoModule));
const MixEtfWithWikifolioModule = dynamic(() =>
    import("src/components/modules/mix-etf-with-wikifolio-module/mix-etf-with-wikifolio-module").then(m => m.MixEtfWithWikifolioModule)
);
const MockupTextModule = dynamic(() => import("src/components/modules/mockup-text-module/mockup-text-module").then(m => m.MockupTextModule));
const MultiListTopWikifoliosModule = dynamic(() =>
    import("src/components/modules/multi-list-top-wikifolios-module/multi-list-wikifolios-module").then(m => m.MultiListTopWikifoliosModule)
);
const PartnerListModule = dynamic(() => import("src/components/modules/partner-list-module/partner-list-module").then(m => m.PartnerListModule));
const PartnerLogosModule = dynamic(() => import("src/components/modules/partner-logos-module/partner-logos-module").then(m => m.PartnerLogosModule));
const PreviewAdvantagesModule = dynamic(() =>
    import("src/components-mui/modules/preview-advantages-module/preview-advantages-module").then(m => m.PreviewAdvantagesModule)
);
const MultiQuoteModule = dynamic(() => import("src/components/modules/quotes-module/multi-quote-module").then(m => m.MultiQuoteModule));
const SingleQuoteModule = dynamic(() => import("src/components/modules/quotes-module/single-quote-module").then(m => m.SingleQuoteModule));
const RegisterModule = dynamic(() => import("src/components/modules/register-module/register-module").then(m => m.RegisterModule));
const SearchModule = dynamic(() => import("src/components/modules/search-module/search-module").then(m => m.SearchModule));
const SelectedWikifoliosModule = dynamic(() =>
    import("src/components/modules/selected-wikifolios/selected-wikifolios-module").then(m => m.SelectedWikifoliosModule)
);
const StepsModule = dynamic(() => import("src/components/modules/steps-module/steps-module").then(m => m.StepsModule));
const TextImageModule = dynamic(() => import("src/components/modules/text-image-module/text-image-module").then(m => m.TextImageModule));
const TwoColumnTextModule = dynamic(() =>
    import("src/components/modules/two-column-text-module/two-column-text-module").then(m => m.TwoColumnTextModule)
);
const UnderlyingListModule = dynamic(() =>
    import("src/components/modules/underlying-lists-module/underlying-list-module").then(m => m.UnderlyingListModule)
);
const VotingModule = dynamic(() => import("src/components/modules/voting-module/voting-module").then(m => m.VotingModule));
const WallOfTextModule = dynamic(() => import("src/components/modules/wall-of-text-module/wall-of-text-module").then(m => m.WallOfTextModule));
const HeroSliderModule = dynamic(() => import("src/components-mui/modules/hero-module/hero-slider-module").then(m => m.HeroSliderModule));
const LeaderBoardModule = dynamic(() => import("src/components-mui/modules/leader-board-module/leader-board-module").then(m => m.LeaderBoardModule));
const WidgetModule = dynamic(() => import("src/components-mui/modules/widget-module/widget-module").then(m => m.WidgetModule));
const WikifolioTableModule = dynamic(() =>
    import("src/components-mui/modules/wikifolio-table/wikifolio-table-module").then(m => m.WikifolioTableModule)
);

// Bottom padding for the header-for-home module. Inlined here (instead of importing SECTION_PB
// from header-for-home-module) so that statically importing the constant does not pull the heavy
// module back into the initial bundle and defeat its dynamic import above.
const HEADER_FOR_HOME_SECTION_PB = ["32px", "48px", "48px", "74px"];

const PARTNER_LOGOS_MODULE_TEST_ID = "partner-logos-module";
const SELECTED_WIKIFOLIOS_MODULE_TEST_ID = "selected-wikifolios-module";
const HEADER_FOR_HOME_MODULE_TEST_ID = "header-for-home-module";

type ModuleHash = IAreaOverviewModule["hash"];

type ModuleRegistry = {
    [TType in ContentModuleType]: (
        moduleProps: Extract<IAreaOverviewModule, { type: TType }>,
        hash: ModuleHash,
        sectionProps: SectionProps | undefined
    ) => React.ReactNode;
};

const moduleRegistry: ModuleRegistry = {
    [ContentModuleType.SPACING]: () => null,
    [ContentModuleType.BLOG_TEASER]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <BlogTeaserModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.PREVIEW_ADVANTAGES]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <PreviewAdvantagesModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.CTA]: (moduleProps, hash, sectionProps) => {
        const { colorScheme, ...ctaProps } = moduleProps;

        return (
            <AreaOverviewModuleContainer
                sectionNavigationId={hash}
                w="100%"
                {...getCtaModuleColorScheme(colorScheme)}
                py={[6, 6, 8]}
                _first={{ mt: 0 }}
                _last={{ mb: 0 }}
                {...sectionProps}
            >
                <CtaModule {...ctaProps} />
            </AreaOverviewModuleContainer>
        );
    },
    [ContentModuleType.FAQ]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <FaqModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.FEES]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <FeesModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.HEADER]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer
            sectionNavigationId={hash}
            w="100%"
            {...getHeaderModuleColorScheme(moduleProps.colorScheme)}
            _first={{ mt: 0 }}
            {...sectionProps}
        >
            <HeaderModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.IMAGE_VIDEO]: (moduleProps, hash, sectionProps) => {
        const mt = !moduleProps.headline && moduleProps.showGrayBg ? [AREA_TOP_MARGIN[0], 0] : undefined;
        const mb = moduleProps.showGrayBg ? [AREA_TOP_MARGIN[0], 0] : undefined;

        return (
            <AreaOverviewModuleContainer sectionNavigationId={hash} _first={{ mt }} _last={{ mb }} {...sectionProps}>
                <ImageVideoModule {...moduleProps} />
            </AreaOverviewModuleContainer>
        );
    },
    [ContentModuleType.PARTNER_LIST]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <PartnerListModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.PARTNER_LOGOS]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} data-test-id={PARTNER_LOGOS_MODULE_TEST_ID} {...sectionProps}>
            <PartnerLogosModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.REGISTER]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer
            sectionNavigationId={hash}
            _first={{ mt: [AREA_TOP_MARGIN[0], AREA_TOP_MARGIN[1], 0] }}
            _last={{ mb: [AREA_BOTTOM_MARGIN[0], AREA_BOTTOM_MARGIN[1], 0] }}
            {...sectionProps}
        >
            <RegisterModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.STEPS]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <StepsModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.TEXT_IMAGE]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} _first={{ mt: 0 }} _last={{ mb: [AREA_BOTTOM_MARGIN[0], 0] }} {...sectionProps}>
            <TextImageModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.WALL_OF_TEXT]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} w="100%" {...sectionProps}>
            <WallOfTextModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.LIST]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <ListModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.MOCKUP_TEXT]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer
            sectionNavigationId={hash}
            _first={{ mt: !moduleProps.headline ? [AREA_TOP_MARGIN[0], AREA_TOP_MARGIN[1], 0] : undefined }}
            _last={{ mb: [AREA_BOTTOM_MARGIN[0], AREA_BOTTOM_MARGIN[1], 0] }}
            {...sectionProps}
        >
            <MockupTextModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.SELECTED_WIKIFOLIO]: (moduleProps, hash, sectionProps) => {
        const { colorScheme = "white" } = moduleProps;
        const isBgWhite = colorScheme === "white";

        return (
            <AreaOverviewModuleContainer
                sectionNavigationId={hash}
                data-test-id={SELECTED_WIKIFOLIOS_MODULE_TEST_ID}
                _first={{ mt: !isBgWhite ? 0 : undefined }}
                _last={{ mb: !isBgWhite ? 0 : undefined }}
                {...sectionProps}
            >
                <SelectedWikifoliosModule {...moduleProps} />
            </AreaOverviewModuleContainer>
        );
    },
    [ContentModuleType.QUOTE]: (moduleProps, hash, sectionProps) =>
        moduleProps.quoteCards.length === 1 ? (
            <AreaOverviewModuleContainer sectionNavigationId={hash} _first={{ mt: [AREA_TOP_MARGIN[0], 0] }} _last={{ mb: 0 }} {...sectionProps}>
                <SingleQuoteModule {...moduleProps} quoteCard={moduleProps.quoteCards[0]} />
            </AreaOverviewModuleContainer>
        ) : (
            <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
                <MultiQuoteModule {...moduleProps} />
            </AreaOverviewModuleContainer>
        ),
    [ContentModuleType.HEADER_FOR_HOME]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer
            sectionNavigationId={hash}
            data-test-id={HEADER_FOR_HOME_MODULE_TEST_ID}
            pb={HEADER_FOR_HOME_SECTION_PB}
            _first={{ mt: 0 }}
            {...sectionProps}
        >
            <HeaderForHomeModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.DISCLAIMER]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <DisclaimerModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.THREE_BESTSELLER]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <ThreeBestsellerModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.SINGLE_BESTSELLER]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <SingleBestSellerModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.JOBS_WIDGET]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <JobsWidgetModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.MARKETO]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <MarketoModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.AUTO_CONTENT]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <AutoContentModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.SEARCH]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <SearchModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.COOKIEBOT_DECLARATION]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <CookiebotDeclarationModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.VOTING]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <VotingModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.BUBBLE_CHART]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <BubbleChartModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.UNDERLYING_LISTS]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <UnderlyingListModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.MIX_ETF_WITH_WIKIFOLIO]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <MixEtfWithWikifolioModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.CHALLENGE_FORM_BVH]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <ChallengeFormModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.LEADER_BOARD]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
            <LeaderBoardModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
    [ContentModuleType.MULTI_LIST]: (moduleProps, hash, sectionProps) => (
        <React.StrictMode>
            <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
                <MultiListTopWikifoliosModule {...moduleProps} />
            </AreaOverviewModuleContainer>
        </React.StrictMode>
    ),
    [ContentModuleType.IMAGE]: (moduleProps, hash, sectionProps) => (
        <React.StrictMode>
            <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
                <ImageModule {...moduleProps} />
            </AreaOverviewModuleContainer>
        </React.StrictMode>
    ),
    [ContentModuleType.TWO_COLUMN_TEXT]: (moduleProps, hash, sectionProps) => (
        <React.StrictMode>
            <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
                <TwoColumnTextModule {...moduleProps} />
            </AreaOverviewModuleContainer>
        </React.StrictMode>
    ),
    [ContentModuleType.WIDGET]: (moduleProps, hash, sectionProps) => (
        <React.StrictMode>
            <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
                <WidgetModule {...moduleProps} />
            </AreaOverviewModuleContainer>
        </React.StrictMode>
    ),
    [ContentModuleType.WIKIFOLIO_TABLE]: (moduleProps, hash, sectionProps) => (
        <React.StrictMode>
            <AreaOverviewModuleContainer sectionNavigationId={hash} {...sectionProps}>
                <WikifolioTableModule {...moduleProps} />
            </AreaOverviewModuleContainer>
        </React.StrictMode>
    ),
    [ContentModuleType.HERO_SLIDER]: (moduleProps, hash, sectionProps) => (
        <AreaOverviewModuleContainer sectionNavigationId={hash} w="100%" _first={{ mt: 0 }} {...sectionProps}>
            <HeroSliderModule {...moduleProps} />
        </AreaOverviewModuleContainer>
    ),
};

interface IAreaOverviewModuleProps {
    moduleProps: IAreaOverviewModule;
    sectionProps?: SectionProps;
}

const getModuleRenderer = (type: ContentModuleType) =>
    moduleRegistry[type] as (moduleProps: IAreaOverviewModule, hash: ModuleHash, sectionProps: SectionProps | undefined) => React.ReactNode;

export const AreaOverviewModule = ({ moduleProps, sectionProps }: IAreaOverviewModuleProps) =>
    getModuleRenderer(moduleProps.type)(moduleProps, moduleProps.hash, sectionProps);
